import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, MapPin, Calendar, ArrowLeft, Trophy, Target } from 'lucide-react';
import axios from 'axios';

const PlayerProfile = () => {
  const { playerId } = useParams();
  const [player, setPlayer] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPlayerData();
  }, [playerId]);

  const fetchPlayerData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get player data from squad endpoint (includes full name)
      const squadResponse = await axios.get('/squad');
      const playerData = squadResponse.data.find(p => p.id === playerId);
      
      if (!playerData) {
        setError('Player not found');
        return;
      }
      
      setPlayer(playerData);
      
      // Try to get player stats (this might fail for public access)
      try {
        const statsResponse = await axios.get(`/players/${playerId}/stats`);
        setStats(statsResponse.data);
      } catch (statsError) {
        console.log('Stats not available for public view');
        setStats(null);
      }
      
    } catch (error) {
      console.error('Error fetching player data:', error);
      setError('Error loading player profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading player profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {error || 'Player not found'}
            </h3>
            <Link to="/squad" className="btn btn-primary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Squad
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/squad"
            className="inline-flex items-center text-white hover:text-gray-300 mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Squad
          </Link>
          
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            <div className="relative">
              {player.photo_url ? (
                <img
                  src={player.photo_url}
                  alt={player.full_name}
                  className="w-32 h-32 md:w-48 md:h-48 object-cover rounded-lg"
                />
              ) : (
                <div className="w-32 h-32 md:w-48 md:h-48 bg-gray-600 flex items-center justify-center rounded-lg">
                  <Users className="h-16 w-16 text-gray-400" />
                </div>
              )}
              <div className="absolute -bottom-2 -right-2 bg-white text-black rounded-full w-12 h-12 flex items-center justify-center font-bold text-lg">
                {player.jersey_number}
              </div>
            </div>
            
            <div className="text-center md:text-left">
              <h1 className="text-4xl font-bold mb-2">{player.full_name}</h1>
              <p className="text-xl text-gray-200 mb-2">#{player.jersey_number}</p>
              <p className="text-lg text-gray-300 mb-4">{player.position}</p>
              
              <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-2 sm:space-y-0">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  {player.nationality}
                </div>
                
                {player.date_of_birth && (
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Born: {new Date(player.date_of_birth).toLocaleDateString()}
                  </div>
                )}
              </div>
              
              <div className="mt-4">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  player.team_type === 'Vets' 
                    ? 'bg-white text-black' 
                    : 'bg-gray-700 text-white'
                }`}>
                  {player.team_type} Team
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Biography */}
            {player.bio && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">About {player.full_name}</h2>
                <p className="text-gray-700 leading-relaxed">{player.bio}</p>
              </div>
            )}

            {/* Stats */}
            {stats && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">Season Statistics</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">{stats.matches_played}</div>
                    <div className="text-sm text-gray-600">Matches Played</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">{stats.goals}</div>
                    <div className="text-sm text-gray-600">Goals</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">{stats.assists}</div>
                    <div className="text-sm text-gray-600">Assists</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-black">{stats.minutes_played}</div>
                    <div className="text-sm text-gray-600">Minutes</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Player Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Player Details</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-sm text-gray-600">Position</div>
                  <div className="font-medium">{player.position}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Jersey Number</div>
                  <div className="font-medium">#{player.jersey_number}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Team</div>
                  <div className="font-medium">{player.team_type}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Nationality</div>
                  <div className="font-medium">{player.nationality}</div>
                </div>
                {player.height && (
                  <div>
                    <div className="text-sm text-gray-600">Height</div>
                    <div className="font-medium">{player.height}</div>
                  </div>
                )}
                {player.weight && (
                  <div>
                    <div className="text-sm text-gray-600">Weight</div>
                    <div className="font-medium">{player.weight}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Social Media Links */}
            {player.social_media && Object.keys(player.social_media).length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Follow {player.full_name}</h3>
                <div className="space-y-2">
                  {Object.entries(player.social_media).map(([platform, url]) => (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-blue-600 hover:text-blue-800 capitalize"
                    >
                      {platform}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerProfile;